import { CustomFormatter } from '@/views/translations/CustomFormatter'
import { Plugin } from 'vue'
import { i18n } from '@/i18n'
import { isNullOrUndefined, isNumber, isSafeInteger, isValidIban, isValidISODate, isValidString } from '@/utils/TypeGuards'
import { timeRangeIsValid } from '@/utils/TimeUtils'
import DbmMonthPicker from '@/components/dbmMonthPicker.vue'
import { isQRIBAN } from '@/utils/IbanUtils'
import parsePhoneNumberFromString, { PhoneNumber } from 'libphonenumber-js'

const t = i18n.global.t

const thousandSeparator = Intl.NumberFormat('de-CH')
  .format(11111)
  .replace(/\p{Number}/gu, '')

// convert "31.12.2023" into a JS Date Object
function parseCHDate(chDate: string): Date {
  const [day, month, year] = chDate.split('.')
  return new Date(`${year}-${month}-${day}`)
}
/**
 * type ValidationRule = (value: number) => string | boolean
 * id like to import  Vuetify/ValidationRule$1[] but i cant
 * */
export type ValidationRule = (...args: any[]) => string | boolean
type RulesType = {
  [key: string]: ValidationRule
}

export const rules: RulesType = {
  required: (value: any) => !!value || t('rules_required'), //we can't use this for booleans because it can't tell the difference between the value false and null
  isNotNull: (value: any) => value != null || t('rules_required_boolean'),
  min8: (v: any) => v.length >= 8 || t('rules_minimum_8_characters'), // as example
  length7: (v: number) => `${v}`.length == 7 || t('rules_exactly_7_characters'),
  isNumeric: (v: any) => {
    if (v === null) return true
    if (v === undefined) return true

    // convert v into string
    if (typeof v === 'number') {
      return true
    }
    if (typeof v === 'string') {
      v = v.trim()
      if (v === '') {
        return true
      }
      if (!v.toString().match(new RegExp('[^\\d.' + thousandSeparator + '-]'))) return true // accepts 12`445.15 as a number and negative numbers as well -12`445.15
      // new RegExp('ab+c', 'i')
    }
    return t('rules_not_a_number')
  },
  isNummericMin(minimum: number) {
    return (value: unknown) => (isNumber(value) && value >= minimum) || t('rules_number_must_be_greater_than', [minimum])
  },
  isInteger(value: unknown) {
    return isSafeInteger(value) || t('rules_not_an_integer')
  },
  notBlank(value: unknown) {
    return isValidString(value) || t('rules_not_blank')
  },
  belowTenMegaAbs(value: any) {
    if (value === null) return true
    if (value === undefined) return true
    // the db field is a decimal(9.2)
    value = parseInt(value.toString().replace(/['’]/g, ''))
    if (value != 'NaN') {
      if (Math.abs(value) < 10000000) return true
    }
    return t('rules_below_ten_mio')
  },
  // chDateStrig = "31.12.2023"
  isMaxTwoMonthsPast: (chDateString: any): boolean | string => {
    if (chDateString === undefined || chDateString === null || chDateString === '') return true
    const [year, month] = [new Date().getFullYear(), new Date().getMonth()]
    const twoMonthAgoTheFirst = new Date(`${year}-${month}-01`).setMonth(month - 2)
    return parseCHDate(chDateString).getTime() <= twoMonthAgoTheFirst ? i18n.t('rules_date_more_than_two_months_ago').toString() : true
  },
  // form, until are IsoDateString or Null or undefined
  isUntilGreaterThanFrom: (from: any, until: any): boolean | string => {
    if (until === undefined || until == null || until.trim() == '') return true
    if (from === undefined || from == null) return i18n.t('DBM_ERROR_160').toString()
    return new Date(until).getTime() < new Date(from).getTime() ? i18n.t('DBM_ERROR_160').toString() : true
  },
  isNumericList: (v: any) => {
    if (v === null || v === '') return true
    // accepts numbers separated by commas
    if (v.toString().match(new RegExp('^[0-9]+(,[0-9]+)*,?$'))) return true
    return t('rules_only_numbers_and_commas')
  },
  isSelected: (v: any) => {
    //v-select: in its undefined state the return value has type 'function' otherwise it can be object or string
    return typeof v == 'string'
  },
  isCHDate: (v: any) => {
    if (v === null || v === undefined) return true
    if (typeof v === 'string') {
      if (v === '') {
        return true
      }

      const parms = v.split(/\./)

      if (parms.length != 3) {
        return t('rules_ch_date_not_a_date_year')
      }
      const yyyy = parseInt(parms[2], 10)
      if (yyyy < 1900) {
        return t('rules_ch_date_not_a_date_year')
      }
      const mm = parseInt(parms[1], 10)
      if (mm < 1 || mm > 12) {
        return t('rules_ch_date_not_a_date_month')
      }
      const dd = parseInt(parms[0], 10)
      if (dd < 1 || dd > 31) {
        return t('rules_ch_date_not_a_date_day')
      }
      const dateCheck = new Date(yyyy, mm - 1, dd)

      return dateCheck.getDate() === dd && dateCheck.getMonth() === mm - 1 && dateCheck.getFullYear() === yyyy ? true : 'not a date'
    }
    return 'not a date'
  },
  isValidMonthRange: (from: any, to: any, range: number) => {
    const toDate = new Date(to)
    const fromDate = new Date(from)
    const formatter = new CustomFormatter()

    const monthsInBetween = toDate.getMonth() - fromDate.getMonth() + 12 * (toDate.getFullYear() - fromDate.getFullYear())
    return monthsInBetween <= range && toDate >= fromDate ? true : formatter.interpolate(t('rules_month_not_a_valid_time_range').toString(), [range.toString()])
  },
  isCHMonth: (v: any) => {
    if (v === null) return true
    v = v.toString()
    if (!v.trim()) return true

    const parms = v.split(/\./)
    const yyyy = parseInt(parms[1], 10)
    if (yyyy < 1900) {
      return t('rules_ch_month_not_a_date_year')
    }
    const mm = parseInt(parms[0], 10)
    if (mm < 1 || mm > 12) {
      return t('rules_ch_month_not_a_date_month')
    }

    const dateCheck = new Date(yyyy, mm - 1, 1)
    return dateCheck.getDate() === 1 && dateCheck.getMonth() === mm - 1 && dateCheck.getFullYear() === yyyy ? true : t('rules_ch_month_not_a_date')
  },
  /**
   * Checks if the start date is before the end date.
   * @param start To check against end date. Is required because {@link DbmMonthPicker} is using its validation rules on the input (e.g. in month '2024.01') and not date picker (date object), so we use this param to provide access to self.
   * @param end To check against start date.
   */
  isBefore(start: InstanceType<typeof DbmMonthPicker>, end: InstanceType<typeof DbmMonthPicker>) {
    return () => {
      if (isNullOrUndefined(start) || isNullOrUndefined(end) || !isValidISODate(end.value)) {
        return true
      }
      const result = timeRangeIsValid(start.value, end.value) || t('rules_date_must_be_before')
      monthPickerValidations.set(start, result === true)
      redoMonthPickerValidation(start, end, { byStart: true })
      return result
    }
  },
  /**
   * Checks if the end date is after the start date
   * @param start To check against the end date
   * @param end To check against the start date. Is required because {@link DbmMonthPicker} is using its validation rules on the input (e.g. in month '2024.01') and date picker (date object), so we use this param to provide access to self.
   * @returns True if the end date is after the start date
   * @see timeRangeIsValid
   */
  isAfter(start: InstanceType<typeof DbmMonthPicker>, end: InstanceType<typeof DbmMonthPicker>) {
    return () => {
      if (isNullOrUndefined(start) || isNullOrUndefined(end) || !isValidISODate(start.value)) {
        return true
      }
      const result = timeRangeIsValid(start.value, end.value) || t('rules_date_must_be_after')
      monthPickerValidations.set(end, result === true)
      redoMonthPickerValidation(start, end, { byEnd: true })
      return result
    }
  },
  /**
   * Validates uploaded images.
   * @param requirements {@link ImageUploadRequirements} requirements for the image
   * @returns `true` if the image is valid, otherwise a string with the (translated) error message.
   */
  image: (requirements: ImageUploadRequirements) => {
    // alias for translation
    return async (files: File[]): Promise<boolean | string> => {
      if (isNullOrUndefined(files)) {
        return requirements.isRequired ? t('image_validator_required') : true
      }
      if (files instanceof File) {
        files = [files]
      }
      if (requirements.isRequired && files.length === 0) {
        return t('image_validator_required')
      }
      for (const file of files) {
        if (requirements.maxSize && file.size > requirements.maxSize) {
          return t('image_validator_max_size', [(requirements.maxSize ?? 0) / 1024])
        }
        if (requirements.minSize && file.size < requirements.minSize) {
          return t('image_validator_min_size', [(requirements.minSize ?? 0) / 1024])
        }
        if (requirements.allowedTypes && requirements.allowedTypes.length > 0 && !requirements.allowedTypes.includes(file.type)) {
          return t('image_validator_allowed_types', [requirements.allowedTypes.join(', ')])
        }
        // load image to get width and height
        const image: HTMLImageElement = new Image()
        image.loading = 'eager'
        image.src = URL.createObjectURL(file)
        // wait for image to be loaded
        await new Promise((res) => (image.onload = res))

        if (requirements.maxWidth && requirements.maxWidth < image.naturalWidth) {
          return t('image_validator_max_width', [requirements.maxWidth])
        }
        if (requirements.minWidth && requirements.minWidth > image.naturalWidth) {
          return t('image_validator_min_width', [requirements.minWidth])
        }
        if (requirements.maxHeight && requirements.maxHeight < image.naturalHeight) {
          return t('image_validator_max_height', [requirements.maxHeight])
        }
        if (requirements.minHeight && requirements.minHeight > image.naturalHeight) {
          return t('image_validator_min_height', [requirements.minHeight])
        }
      }
      return true
    }
  },
  /**
   * Is valid IBAN
   * @param v
   */
  iban: (v: string): true | string => {
    return isValidIban(v) || t('rules_iban_invalid')
  },
  /**
   * Is valid QR-IBAN
   * @see isValidIban
   */
  ibanQrr: (v: string): true | string => {
    return isQRIBAN(v) || t('rules_iban_qrr_invalid')
  },
  /**
   * Checks if the value is a valid phone number.
   *
   * @param v - The phone number to validate.
   * @returns `true` if the phone number is valid, otherwise a string with the (translated) error message.
   */
  isValidPhoneNumber(v: string | unknown): string | boolean {
    if (v === null || v === undefined || typeof v != 'string' || v == '') return true
    // phone number have to start with '+' and no spaces, and should only contain digits
    if (!v.startsWith('+') || v.includes(' ') || v.substring(1, v.length).match(/[^$,.\d]/)) {
      // theoretically isNaN('+41') is false and could be used instead of regex,
      // and we would not need to check if it starts with '-' (isNaN('-41') is also false) since we already check if it starts with '+',
      // but isNaN is expecting a number and not a string
      return t('DBM_ERROR_311')
    }

    const parsed: PhoneNumber | undefined = parsePhoneNumberFromString(v)
    if (parsed != undefined && parsed.isValid()) {
      // now check if the phone number was written in international format
      return parsed.number === v || t('DBM_ERROR_311')
    }
    return t('DBM_ERROR_311')
  }
}

/**
 * @deprecated use rules without $ like: import rules from rules
 */
export const $rules = rules

export const Rules: Plugin = {
  install(app: any) {
    app.config.globalProperties.$rules = $rules
  }
}

/**
 * Re-triggers the validation of the DbmMonthPicker but avoids validation loop.
 *
 * @param start Start date picker
 * @param end End date picker
 * @param byStart if true, origin set by start date picker
 * @param byEnd if true, origin set by end date picker
 */
const redoMonthPickerValidation = (
  start: InstanceType<typeof DbmMonthPicker>,
  end: InstanceType<typeof DbmMonthPicker>,
  { byStart, byEnd }: { byStart?: boolean; byEnd?: boolean }
) => {
  const startValidation: boolean = Boolean(monthPickerValidations.get(start))
  const endValidation: boolean = Boolean(monthPickerValidations.get(end))
  if (startValidation !== endValidation) {
    // only trigger when both validations are not the same
    if (byStart) {
      end.validate()
    } else if (byEnd) {
      start.validate()
    }
  }
}

/** Stores the results of validation (states) of a monthPicker instance */
const monthPickerValidations: Map<InstanceType<typeof DbmMonthPicker>, boolean> = new Map<InstanceType<typeof DbmMonthPicker>, boolean>()

export interface ImageUploadRequirements {
  /** True = file upload needed, false = no file upload needed, default false */
  isRequired?: boolean
  /** Maximum size in bytes, default any */
  maxSize?: number
  /** Minimum size in bytes, default any */
  minSize?: number
  /** Allowed file types, default any */
  allowedTypes?: string[]
  /** Maximum width in pixels, default any */
  maxWidth?: number
  /** Minimum width in pixels, default any */
  minWidth?: number
  /** Maximum height in pixels, default any */
  maxHeight?: number
  /** Minimum height in pixels, default any */
  minHeight?: number
}
